import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@material-ui/core';
import { 
  CheckCircle, 
  Error, 
  Build,
  Videocam,
  VideocamOff
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100vh',
    width: '100vw',
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#2e7d32',
    color: 'white',
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    height: '15vh'
  },
  scannerDisplay: {
    backgroundColor: '#424242',
    color: '#fff',
    padding: theme.spacing(2),
    textAlign: 'center',
    fontSize: '2.5rem',
    fontFamily: 'monospace',
    height: '15vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius
  },
  statusCard: {
    height: '25vh',
    display: 'flex',
    flexDirection: 'column'
  },
  statusContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  successCard: {
    borderLeft: `8px solid ${theme.palette.success.main}`
  },
  errorCard: {
    borderLeft: `8px solid ${theme.palette.error.main}`
  },
  cameraCard: {
    textAlign: 'center',
    padding: theme.spacing(1),
    backgroundColor: '#1a1a1a',
    color: 'white',
    height: '25vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  },
  cameraVideo: {
    width: '100%',
    height: '70%',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#000'
  },
  cameraLabel: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    backgroundColor: 'rgba(244, 67, 54, 0.9)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  cameraError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '70%',
    color: '#666'
  },
  recentScans: {
    height: '35vh',
    overflow: 'hidden'
  },
  recentList: {
    height: 'calc(35vh - 80px)',
    overflow: 'hidden',
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius
  },
  readyToScan: {
    color: '#4caf50',
    fontSize: '2.5rem'
  },
  equipmentIcon: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1)
  },
  listItem: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  }
}));

const EquipmentCheckin = () => {
  const classes = useStyles();
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanned, setLastScanned] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        // Check if permission is already granted
        const permission = await navigator.permissions.query({ name: 'camera' });
        
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user', // Front-facing camera
            width: { ideal: 320 },
            height: { ideal: 240 }
          }
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
        }
        setCameraError(false);
      } catch (error) {
        console.log('Camera access denied or unavailable:', error);
        setCameraError(true);
      }
    };

    // Delay camera init slightly to let the page load
    const timer = setTimeout(initCamera, 1000);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle keyboard input from scanner
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Enter') {
        // Process the scan
        if (scanBuffer.length > 0) {
          processScan(scanBuffer);
          setScanBuffer('');
        }
      } else {
        // Add character to buffer
        setScanBuffer(prev => prev + e.key);
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [scanBuffer]);

  // Ensure focus and prevent sleep/idle
  useEffect(() => {
    // Focus the window immediately
    window.focus();
    
    // Set focus every few seconds to ensure scanner input works
    const focusInterval = setInterval(() => {
      window.focus();
      document.body.focus();
    }, 5000);

    // Prevent page from going idle
    const keepAlive = setInterval(() => {
      // Small visual indicator that system is active
      document.body.style.opacity = '0.999';
      setTimeout(() => {
        document.body.style.opacity = '1';
      }, 50);
    }, 30000);

    // Handle visibility change (when tab becomes active/inactive)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => {
          window.focus();
          document.body.focus();
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(focusInterval);
      clearInterval(keepAlive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const processScan = async (scannedData) => {
    console.log('scanned data...', scannedData)
    setLoading(true);
    setLastScanned(scannedData);
    
    try {
      // Call your API to register the equipment check-in
      const response = await sendToServer(scannedData);
      
      if (response.success) {
        const newItem = {
          id: Date.now(),
          equipmentId: scannedData,
          name: response.data.name || `Equipment ${scannedData}`,
          timestamp: new Date().toLocaleTimeString(),
          status: 'returned'
        };

        // Keep only last 5 scans
        setScannedItems(prev => [newItem, ...prev].slice(0, 5));
        setCurrentItem(newItem);
      } else {
        setCurrentItem({ error: true, equipmentId: scannedData });
      }
    } catch (error) {
      setCurrentItem({ error: true, equipmentId: scannedData });
    }
    
    setLoading(false);
  };

  // Replace this with your actual API call
  const sendToServer = async (equipmentId) => {
    // Your actual API call here
    const response = await fetch('/api/equipment/checkin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        equipmentId: equipmentId,
        timestamp: new Date().toISOString(),
        location: 'shed'
      })
    });
    
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    
    return await response.json();
    
    // Mock for now - remove this when you connect to your API
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: Math.random() > 0.1, // 90% success rate for demo
          data: {
            name: `Tool ${equipmentId.slice(-4)}`,
            description: 'Equipment returned to shed'
          }
        });
      }, 600);
    });
  };

  return (
    <div className={classes.container}>
      {/* Header */}
      <Paper className={classes.header} elevation={3}>
        <Build className={classes.equipmentIcon} />
        <Typography variant="h4" component="h1">
          Equipment Return Kiosk
        </Typography>
        <Typography variant="h6">
          Scan your equipment barcode below
        </Typography>
      </Paper>

      {/* Scanner Display */}
      <Paper className={classes.scannerDisplay} elevation={3}>
        {loading ? (
          <CircularProgress size={60} color="inherit" />
        ) : scanBuffer ? (
          scanBuffer
        ) : lastScanned ? (
          `Last: ${lastScanned}`
        ) : (
          <span className={classes.readyToScan}>Ready to Scan</span>
        )}
      </Paper>

      <Grid container spacing={2}>
        {/* Current Item Status */}
        <Grid item xs={8}>
          <Card className={`${classes.statusCard} ${currentItem?.error ? classes.errorCard : classes.successCard}`}>
            <CardContent className={classes.statusContent}>
              {currentItem?.error ? (
                <>
                  <Typography variant="h4" gutterBottom>
                    <Error color="error" style={{ marginRight: 8 }} />
                    Not Recognized
                  </Typography>
                  <Typography variant="h5">
                    ID: {currentItem.equipmentId}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Please check barcode and try again
                  </Typography>
                </>
              ) : currentItem ? (
                <>
                  <Typography variant="h4" gutterBottom>
                    <CheckCircle color="primary" style={{ marginRight: 8 }} />
                    Equipment Returned
                  </Typography>
                  <Typography variant="h5" style={{ marginBottom: 8 }}>
                    {currentItem.name}
                  </Typography>
                  <Typography variant="h6" color="textSecondary">
                    ID: {currentItem.equipmentId}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Logged at {currentItem.timestamp}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h4" gutterBottom color="textSecondary">
                    Waiting for Equipment Scan
                  </Typography>
                  <Typography variant="h6" color="textSecondary">
                    Point scanner at barcode and scan
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Camera Monitor */}
        <Grid item xs={4}>
          <Card className={classes.cameraCard}>
            <div className={classes.cameraLabel}>
              <Videocam style={{ fontSize: '1rem' }} />
              RECORDING
            </div>
            {cameraError ? (
              <div className={classes.cameraError}>
                <VideocamOff style={{ fontSize: '2rem', marginBottom: 8 }} />
                <Typography variant="body2">
                  Camera Unavailable
                </Typography>
              </div>
            ) : (
              <video
                ref={videoRef}
                className={classes.cameraVideo}
                autoPlay
                muted
                playsInline
              />
            )}
            <Typography variant="body2" style={{ marginTop: 8, fontSize: '0.8rem' }}>
              Security Monitor
            </Typography>
          </Card>
        </Grid>

        {/* Recent Scans */}
        <Grid item xs={12}>
          <Card className={classes.recentScans}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Recent Returns
              </Typography>
              
              {scannedItems.length === 0 ? (
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center" 
                  height="calc(35vh - 120px)"
                >
                  <Typography variant="h6" color="textSecondary">
                    No equipment scanned yet today
                  </Typography>
                </Box>
              ) : (
                <Paper className={classes.recentList}>
                  <List dense>
                    {scannedItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ListItem className={classes.listItem}>
                          <ListItemText
                            primary={
                              <Typography variant="h6">
                                {item.name} ({item.equipmentId})
                              </Typography>
                            }
                            secondary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">
                                  {item.timestamp}
                                </Typography>
                                <Chip 
                                  label="RETURNED" 
                                  color="primary" 
                                  size="small"
                                  style={{ fontWeight: 'bold' }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < scannedItems.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default EquipmentCheckin;
